import apiClient from 'core/api/client';
import {
  assertImageFileSize,
  multipartRequestConfig,
  pollUploadJob,
} from 'shared/lib/uploadJob';
import type {
  DriverLicenceStatusResponse,
  DriverProfile,
  DriversMeResponse,
  SubmitDriverVerificationBody,
} from 'types/driver.types';
import type {
  DriverLicenceUploadEnqueueResponse,
  DriverLicenceUploadJobResponse,
  VehicleType,
} from 'types/api.generated';

export const getMyDriverProfiles = async (): Promise<DriversMeResponse> => {
  const { data } = await apiClient.get<DriversMeResponse>('/api/v1/drivers/me');
  return data;
};

export const getMyLicenceStatus = async (
  organizationId: string,
): Promise<DriverLicenceStatusResponse> => {
  const { data } = await apiClient.get<DriverLicenceStatusResponse>(
    '/api/v1/drivers/me/licence-status',
    { params: { organizationId } },
  );
  return data;
};

export const getOrgDriverLicenceStatus = async (
  userId: string,
  orgId?: string,
): Promise<DriverLicenceStatusResponse> => {
  const { data } = await apiClient.get<DriverLicenceStatusResponse>(
    `/api/v1/organizations/drivers/${userId}/licence-status`,
    orgId ? { headers: { 'X-Org-Id': orgId } } : undefined,
  );
  return data;
};

export const submitDriverVerification = async (
  body: SubmitDriverVerificationBody,
): Promise<DriverProfile> => {
  const { data } = await apiClient.post<DriverProfile>(
    '/api/v1/drivers/me/verification',
    body,
  );
  return data;
};

export const enqueueDriverLicenceDocument = async (
  file: File,
  vehicleType: VehicleType,
  orgId: string,
): Promise<DriverLicenceUploadEnqueueResponse> => {
  assertImageFileSize(file);
  const form = new FormData();
  form.append('image', file);
  form.append('vehicleType', vehicleType);

  const { data } = await apiClient.post<DriverLicenceUploadEnqueueResponse>(
    '/api/v1/drivers/me/verification/document',
    form,
    {
      ...multipartRequestConfig,
      headers: {
        ...multipartRequestConfig.headers,
        'X-Org-Id': orgId,
      },
    },
  );
  return data;
};

export const getDriverLicenceUploadJob = async (
  jobId: string,
  orgId: string,
): Promise<DriverLicenceUploadJobResponse> => {
  const { data } = await apiClient.get<DriverLicenceUploadJobResponse>(
    `/api/v1/drivers/me/verification/upload-jobs/${jobId}`,
    { headers: { 'X-Org-Id': orgId } },
  );
  return data;
};

export const uploadDriverLicenceDocumentAndWait = async (
  file: File,
  vehicleType: VehicleType,
  orgId: string,
): Promise<string> => {
  const { jobId } = await enqueueDriverLicenceDocument(file, vehicleType, orgId);
  return pollUploadJob(async () => {
    const job = await getDriverLicenceUploadJob(jobId, orgId);
    return { status: job.status, url: job.url, error: job.error };
  });
};
